/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { ButtonProps, IconProps } from '@coursera/cds-core';
import { Typography2, breakpoints } from '@coursera/cds-core';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import ModuleLayout, { isHorizontalLayout } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { ImageType } from 'bundles/cds-labs/components/Modules/components/Image';
import Image from 'bundles/cds-labs/components/Modules/components/Image';
import PreservedUtmButton from 'bundles/cds-labs/components/Modules/components/PreservedUtmButton';

type Icon = React.ReactElement<IconProps>;

export type ValuePropItem = {
  id: string;
  description?: React.ReactNode | null;
  headline?: string | null;
  icon?: ImageType | Icon | null;
  ctaText?: string | null;
  ctaTarget?: string | null;
  ctaVariant?: string | null;
};

type Props = {
  description?: React.ReactNode | null;
  headline?: string | null;
  valueProps?: ValuePropItem[];
  cardBackgroundColor?: string;
} & ModuleLayoutConfig;

const styles = {
  moduleHeadline: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  moduleHeadlineNoBottomMargin: css`
    margin-bottom: calc(-1 * var(--cds-spacing-200));
  `,
  moduleDescription: css`
    p {
      margin-bottom: 0 !important;
    }
  `,
  cardDescription: css`
    p {
      margin-bottom: 0 !important;
    }
  `,
  cards: css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: var(--cds-spacing-400);

    ${breakpoints.down('xs')} {
      gap: var(--cds-spacing-100);
    }
  `,

  headingVerticalLayout: css`
    text-align: center;

    ${breakpoints.down('xs')} {
      margin-bottom: var(--cds-spacing-100);
    }
  `,

  card: (cardBasis: string, cardBackgroundColor?: string) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: var(--cds-spacing-300);
    background-color: ${cardBackgroundColor ?? 'var(--cds-color-neutral-background-primary)'};
    color: ${cardBackgroundColor ? 'inherit' : 'var(--cds-color-neutral-primary)'};
    border-radius: var(--cds-border-radius-100);
    gap: var(--cds-spacing-150);
    flex-basis: calc(${cardBasis} - var(--cds-spacing-400));

    ${breakpoints.down('xs')} {
      flex-basis: 100%;
      flex-direction: row;
      align-items: flex-start;
      text-align: left;
    }
  `,

  cardContent: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
  `,

  cardHorizontalLayout: css`
    align-items: flex-start;
    text-align: left;
  `,

  cardTwoColumns: css`
    flex-basis: calc(50% - var(--cds-spacing-400));
  `,

  ctaSection: css`
    margin-top: auto;
    padding-top: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      .cds-button-secondary,
      .cds-button-ghost {
        width: 100%;
        max-width: 100%;
      }
    }
  `,
};

export function ValuePropsModule(props: Props) {
  const isHorizontal = isHorizontalLayout(props);
  const track = useTracker();

  const trackClick = (trackingName: ButtonName, linkTarget?: string) => () => {
    track('click_merchandising_module', {
      button: { name: trackingName, linkURL: linkTarget ?? '' },
      merchandisingModule: {
        moduleType: 'value_props_module',
      },
    });
  };

  // dynamically decide the card width
  const numCards = props.valueProps?.length || 0;
  let cardBasis = '50%';
  if (!isHorizontal && numCards > 4) {
    cardBasis = '33.3%';
  } else if (!isHorizontal) {
    cardBasis = `${100 / numCards}%`;
  }

  const cardPanel = () => (
    <div css={styles.cards}>
      {props.valueProps?.map((card) => {
        if (!card) return null;

        let icon: React.ReactNode = null;
        if (card.icon && 'url' in card.icon) {
          // the icon is an image object
          icon = <Image {...card.icon} />;
        } else if (card.icon) {
          // the icon is a CDS icon
          icon = card.icon as Icon;
        }

        return (
          <div
            css={[styles.card(cardBasis, props.cardBackgroundColor), isHorizontal ? styles.cardHorizontalLayout : null]}
            key={card.id}
          >
            {icon}
            <div css={styles.cardContent}>
              <Typography2 variant="subtitleLarge" component="h3" color="inherit">
                {card?.headline}
              </Typography2>
              <Typography2 variant="bodyPrimary" component="div" color="inherit" css={styles.cardDescription}>
                {card?.description}
              </Typography2>
              {card?.ctaText && card?.ctaTarget && card?.ctaVariant && (
                <div css={styles.ctaSection}>
                  <PreservedUtmButton
                    variant={card.ctaVariant as ButtonProps['variant']}
                    href={card.ctaTarget}
                    trackingName="primary_cta"
                    onClick={trackClick('primary_cta', card.ctaTarget)}
                  >
                    {card.ctaText}
                  </PreservedUtmButton>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const contentPanel = () => {
    // if title and description are empty, don't render anything to prevent extra spacing
    if (!props.description && !props.headline) {
      return null;
    }
    return (
      <div css={[isHorizontal ? null : styles.headingVerticalLayout]}>
        {props.headline && (
          <Typography2
            variant="titleSmall"
            component="h2"
            color="inherit"
            css={props.description ? styles.moduleHeadline : styles.moduleHeadlineNoBottomMargin}
          >
            {props.headline}
          </Typography2>
        )}

        {props.description && (
          <Typography2 variant="bodyPrimary" component="div" color="inherit" css={styles.moduleDescription}>
            {props.description}
          </Typography2>
        )}
      </div>
    );
  };

  return <ModuleLayout assembly={props} renderContent={contentPanel} renderImage={cardPanel} />;
}

export default ValuePropsModule;
